import React from "react";
import { Box } from "@mui/material";
import { replaceTemplateTagsForPreview } from "Util/notificationTemplatePreview";
import useStyles from "./styles";

interface SMSPreviewProps {
  content?: string;
  serviceName?: string;
  className?: string;
  showRawTags?: boolean;
}

const SMSPreview: React.FC<SMSPreviewProps> = ({
  content = "",
  serviceName = "TM-SHIPEEZI",
  className,
  showRawTags = false,
}) => {
  const classes = useStyles();

  const previewText = React.useMemo(() => {
    if (!content) return "";
    return replaceTemplateTagsForPreview(content, showRawTags, false);
  }, [content, showRawTags]);

  const dynamicFontSize = React.useMemo(() => {
    const charCount = previewText.length;
    if (charCount <= 80) return 13.4;
    if (charCount >= 168) return 9;
    const ratio = (charCount - 80) / (168 - 80);
    return 13.4 - (ratio * 4.4);
  }, [previewText]);

  const dynamicLineHeight = React.useMemo(() => {
    return `${dynamicFontSize * 1.5}px`;
  }, [dynamicFontSize]);

  const currentTime = React.useMemo(() => {
    const now = new Date();
    return now.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: false,
    });
  }, []);

  const messageTimestamp = React.useMemo(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }, []);

  return (
    <Box className={`${classes.phoneContainer} ${className || ""}`}>
      <Box className={classes.phoneFrame}>
        <Box className={classes.phoneScreen}>
          {/* Status Bar */}
          <Box className={classes.statusBar}>
            <Box className={classes.statusBarLeft}>{currentTime}</Box>
            <Box className={classes.dynamicIsland} />
            <Box className={classes.statusBarRight}>
              {/* Signal Icon */}
              <Box className={classes.statusBarIcon}>
                <svg width="15" height="10" viewBox="0 0 15 10" fill="none">
                  <path
                    d="M1 9L3 7M5 9L7 5M9 9L11 3M13 9L15 1"
                    stroke="#000"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </Box>
              {/* Wifi Icon */}
              <Box className={classes.statusBarIcon}>
                <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
                  <path
                    d="M7 9C7.55228 9 8 8.55228 8 8C8 7.44772 7.55228 7 7 7C6.44772 7 6 7.44772 6 8C6 8.55228 6.44772 9 7 9Z"
                    fill="#000"
                  />
                  <path
                    d="M3.5 5.5C4.88071 4.11929 6.88071 4.11929 8.26143 5.5"
                    stroke="#000"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                  />
                  <path
                    d="M1 3C3.68629 0.313708 7.31371 0.313708 10 3"
                    stroke="#000"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                  />
                </svg>
              </Box>
              {/* Battery Icon */}
              <Box className={classes.statusBarIcon}>
                <svg width="23" height="11" viewBox="0 0 23 11" fill="none">
                  <rect x="1" y="2" width="19" height="7" rx="1" stroke="#000" strokeWidth="1.2" />
                  <rect x="20" y="4" width="1.5" height="3" fill="#000" />
                  <rect x="2.5" y="3.5" width="15" height="5" rx="0.5" fill="#000" />
                </svg>
              </Box>
            </Box>
          </Box>

          {/* Navigation Header */}
          <Box className={classes.navigationHeader}>
            <Box className={classes.navLeft}>
              {/* Back Arrow Icon */}
              <Box className={classes.navBackIcon}>
                <svg width="15" height="20" viewBox="0 0 15 20" fill="none">
                  <path
                    d="M10 15L5 10L10 5"
                    stroke="#000"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Box>
            </Box>
            <Box className={classes.navCenter}>
              <Box className={classes.navAvatar}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="8" r="4" fill="#98A2B3" />
                  <path
                    d="M6 20C6 16 9 14 12 14C15 14 18 16 18 20"
                    stroke="#98A2B3"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </Box>
              <Box className={classes.navNameRow}>
                <Box component="span" className={classes.navServiceName}>
                  {serviceName}
                </Box>
                <Box className={classes.navChevron}>
                  <svg width="4" height="6" viewBox="0 0 4 6" fill="none">
                    <path
                      d="M1 1L3 3L1 5"
                      stroke="#000"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Box>
              </Box>
            </Box>
            <Box className={classes.navRight} />
          </Box>

          {/* Messages Container */}
          <Box className={classes.messagesContainer}>
            {previewText ? (
              <Box className={classes.messageGroup}>
                <Box className={classes.messageTimestamp}>
                  <Box component="span" className={classes.timestampText}>
                    Yesterday 9:41
                  </Box>
                </Box>
                <Box className={classes.messageBubbleContainer}>
                  <Box className={classes.messageBubble}>
                    <Box 
                      component="p" 
                      className={classes.messageText}
                      sx={{
                        fontSize: `${dynamicFontSize}px`,
                        lineHeight: dynamicLineHeight,
                      }}
                    >
                      {previewText}
                    </Box>
                  </Box>
                  {/* Message Tail */}
                  <Box className={classes.messageBubbleTail}>
                    <svg width="14" height="17" viewBox="0 0 14 17" fill="none" preserveAspectRatio="none">
                      <path
                        d="M0 0C0 4 4 4 4 4V13C4 13 0 13 0 17V0Z"
                        fill="#E9E9EB"
                      />
                    </svg>
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box className={classes.emptyState}>
                Start typing to see your SMS preview...
              </Box>
            )}
          </Box>

          {/* Bottom Input Section */}
          <Box className={classes.bottomSection}>
            <Box className={classes.inputContainer}>
              <Box className={classes.inputActions}>
                {/* Camera Icon */}
                <Box className={classes.cameraIcon}>
                  <svg width="32" height="24" viewBox="0 0 32 24" fill="none">
                    <rect x="4" y="6" width="24" height="16" rx="2" stroke="#000" strokeWidth="1.5" />
                    <circle cx="16" cy="14" r="3" stroke="#000" strokeWidth="1.5" />
                    <circle cx="24" cy="8" r="1.5" fill="#000" />
                  </svg>
                </Box>
              </Box>
              <Box className={classes.inputField}>
                <Box className={classes.inputPlaceholder}>SMS/MMS</Box>
                {/* Mic Icon */}
                <Box className={classes.inputSendIcon}>
                  <svg width="10" height="15" viewBox="0 0 10 15" fill="none">
                    <path
                      d="M5 1V8M5 8C6.65685 8 8 6.65685 8 5V2C8 0.343146 6.65685 -1 5 -1C3.34315 -1 2 0.343146 2 2V5C2 6.65685 3.34315 8 5 8Z"
                      stroke="#FFF"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M2 10V12C2 14.2091 3.79086 16 6 16H4C6.20914 16 8 14.2091 8 12V10"
                      stroke="#FFF"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                    />
                  </svg>
                </Box>
              </Box>
            </Box>
            <Box className={classes.homeIndicator} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SMSPreview;

